import * as React from 'react';

import { getIntegrationBlockForURL } from '@gitbook/app-database';
import { BLOCK_INTEGRATION, type BlockEmbedData, integration } from '@gitbook/doc-core';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { filterOutNullable } from '@gitbook/util-ts';

import { EditableCaption, useCaption } from '../caption';
import {
    BlockLoadingFallback,
    BlockWrapper,
    useBlockLayoutGroupFactory,
    useBlockPaletteFactory,
    VoidBlockFrame,
} from '../common';
import { EmbedView } from './EmbedView';
import { useSpaceIntegrationsBlocks } from './IntegrationBlocks';
import { IntegrationBlockView } from './IntegrationBlockView';

/**
 * Component to render an embed block.
 */
export function BlockEmbed(props: BlockProps): React.ReactElement {
    const { node, editor } = props;
    const nodeKey = node.key;

    const createLayoutGroup = useBlockLayoutGroupFactory({ nodeKey });
    const caption = useCaption(node, editor);

    const palette = useBlockPaletteFactory({
        nodeKey,
        createExtraGroups: () => {
            return [
                createLayoutGroup(),
                {
                    key: 'types',
                    commands: [
                        {
                            key: 'caption',
                            title: caption.hasCaption ? 'Edit caption' : 'Add caption',
                            icon: Icon.Caption,
                            trigger: () => {
                                caption.selectCaption();
                            },
                        },
                        caption.hasCaption
                            ? {
                                  key: 'caption-remove',
                                  title: 'Remove caption',
                                  icon: Icon.CaptionRemove,
                                  trigger: () => {
                                      caption.removeCaption();
                                  },
                              }
                            : null,
                    ].filter(filterOutNullable),
                },
            ].filter(filterOutNullable);
        },
    });

    return (
        <BlockWrapper palette={palette} {...props}>
            <VoidBlockFrame {...props}>
                <React.Suspense fallback={<BlockLoadingFallback />}>
                    <BlockEmbedContent {...props} />
                </React.Suspense>
            </VoidBlockFrame>
            <EditableCaption {...props} />
        </BlockWrapper>
    );
}

/**
 * Inner content of the block, wrapped in a suspense boundary.
 * If an integration is found for the URL, it will render it as an integration block
 * and mutate the block.
 */
function BlockEmbedContent(props: BlockProps): React.ReactElement {
    const { node, editor } = props;
    const { readOnly } = editor;
    const data = node.data.toFrozenObject<BlockEmbedData>();

    // Find if there are an installed integration that match this url
    // and if there is, use it to render the embed
    const spaceIntegrationBlocks = useSpaceIntegrationsBlocks();

    const integrationBlockData = React.useMemo(() => {
        const match = getIntegrationBlockForURL(spaceIntegrationBlocks, data.url);

        if (!match) {
            return null;
        }

        return integration.createIntegrationBlockDataForLinkUnfurl(
            match.integrationKey,
            match.block.id,
            data.url
        );
    }, [spaceIntegrationBlocks, data.url]);

    // If the embed could be an integration block, we transform the block.
    React.useEffect(() => {
        if (!integrationBlockData || readOnly) {
            return;
        }

        editor.change((change) => {
            change.setNodeByKey(node.key, {
                type: BLOCK_INTEGRATION,
                data: integrationBlockData,
            });
        });
    }, [editor, readOnly, integrationBlockData, node.key]);

    // If there is an integration block, render it
    if (integrationBlockData) {
        return (
            <IntegrationBlockView
                data={integrationBlockData}
                readOnly={readOnly}
                onDataChange={(data) => {
                    editor.change((change) => {
                        change.setNodeByKey(node.key, {
                            type: BLOCK_INTEGRATION,
                            data,
                        });
                    });
                }}
            />
        );
    }

    return <EmbedView url={data.url} readOnly={readOnly} />;
}
